import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, XCircle, Clock, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { DashboardLayout } from '@/components/layout/dashboard-layout';

interface TeacherRequest {
  id: string;
  user_id: string;
  status: 'pending' | 'approved' | 'rejected';
  request_message: string;
  rejection_reason: string | null;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  profiles: {
    full_name: string | null;
    email: string;
    avatar_url: string | null;
  };
}

export default function TeacherRequestsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<TeacherRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<TeacherRequest | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('teacher_requests')
        .select(`
          *,
          profiles:user_id (
            full_name,
            email,
            avatar_url
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setRequests(data || []);
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: 'Impossibile caricare le richieste',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (request: TeacherRequest, type: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setActionType(type);
    setRejectionReason('');
  };

  const confirmAction = async () => {
    if (!selectedRequest || !actionType) return;

    if (actionType === 'reject' && !rejectionReason.trim()) {
      toast({
        title: 'Errore',
        description: 'Inserisci un motivo per il rifiuto',
        variant: 'destructive',
      });
      return;
    }

    setProcessing(true);

    try {
      const updateData: any = {
        status: actionType === 'approve' ? 'approved' : 'rejected',
        reviewed_by: user?.id,
        reviewed_at: new Date().toISOString(),
      };

      if (actionType === 'reject') {
        updateData.rejection_reason = rejectionReason.trim();
      }

      const { error } = await supabase
        .from('teacher_requests')
        .update(updateData)
        .eq('id', selectedRequest.id);

      if (error) throw error;

      toast({
        title: 'Richiesta processata',
        description: `La richiesta è stata ${actionType === 'approve' ? 'approvata' : 'rifiutata'} con successo`,
      });

      loadRequests();
      setSelectedRequest(null);
      setActionType(null);
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: error.message || 'Impossibile processare la richiesta',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
            <Clock className="w-3 h-3 mr-1" />
            In attesa
          </Badge>
        );
      case 'approved':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approvata
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
            <XCircle className="w-3 h-3 mr-1" />
            Rifiutata
          </Badge>
        );
      default:
        return null;
    }
  };

  const filterRequestsByStatus = (status: string) => {
    return requests.filter((req) => req.status === status);
  };

  const RequestCard = ({ request }: { request: TeacherRequest }) => (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              {request.profiles.avatar_url ? (
                <img
                  src={request.profiles.avatar_url}
                  alt={request.profiles.full_name || 'User'}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <User className="w-6 h-6 text-blue-600" />
              )}
            </div>
            <div>
              <h3 className="font-semibold">
                {request.profiles.full_name || 'Utente'}
              </h3>
              <p className="text-sm text-gray-600">{request.profiles.email}</p>
            </div>
          </div>
          {getStatusBadge(request.status)}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-sm font-medium">Messaggio</Label>
          <p className="mt-1 text-gray-700 bg-gray-50 p-3 rounded-lg text-sm">
            {request.request_message}
          </p>
        </div>

        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            Richiesta il{' '}
            {new Date(request.created_at).toLocaleDateString('it-IT', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        </div>

        {request.status === 'rejected' && request.rejection_reason && (
          <div>
            <Label className="text-sm font-medium text-red-700">Motivo del rifiuto</Label>
            <p className="mt-1 text-red-600 bg-red-50 p-3 rounded-lg border border-red-200 text-sm">
              {request.rejection_reason}
            </p>
          </div>
        )}

        {request.status === 'pending' && (
          <div className="flex gap-2 pt-2">
            <Button
              onClick={() => handleAction(request, 'approve')}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Approva
            </Button>
            <Button
              onClick={() => handleAction(request, 'reject')}
              variant="destructive"
              className="flex-1"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Rifiuta
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  const pendingRequests = filterRequestsByStatus('pending');
  const approvedRequests = filterRequestsByStatus('approved');
  const rejectedRequests = filterRequestsByStatus('rejected');

  return (
    <DashboardLayout>
      <div className="py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Richieste Docenti</h1>
          <p className="text-gray-600">
            Gestisci le richieste degli utenti per diventare docenti
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="pending" className="relative">
              In attesa
              {pendingRequests.length > 0 && (
                <Badge className="ml-2 bg-yellow-500">{pendingRequests.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved">
              Approvate
              {approvedRequests.length > 0 && (
                <Badge className="ml-2 bg-green-500">{approvedRequests.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="rejected">
              Rifiutate
              {rejectedRequests.length > 0 && (
                <Badge className="ml-2 bg-red-500">{rejectedRequests.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-4">
            {pendingRequests.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Nessuna richiesta in attesa</p>
                </CardContent>
              </Card>
            ) : (
              pendingRequests.map((request) => (
                <RequestCard key={request.id} request={request} />
              ))
            )}
          </TabsContent>

          <TabsContent value="approved" className="space-y-4">
            {approvedRequests.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Nessuna richiesta approvata</p>
                </CardContent>
              </Card>
            ) : (
              approvedRequests.map((request) => (
                <RequestCard key={request.id} request={request} />
              ))
            )}
          </TabsContent>

          <TabsContent value="rejected" className="space-y-4">
            {rejectedRequests.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <XCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Nessuna richiesta rifiutata</p>
                </CardContent>
              </Card>
            ) : (
              rejectedRequests.map((request) => (
                <RequestCard key={request.id} request={request} />
              ))
            )}
          </TabsContent>
        </Tabs>

        <Dialog
          open={!!selectedRequest && !!actionType}
          onOpenChange={() => {
            setSelectedRequest(null);
            setActionType(null);
            setRejectionReason('');
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionType === 'approve' ? 'Approva richiesta' : 'Rifiuta richiesta'}
              </DialogTitle>
              <DialogDescription>
                {actionType === 'approve'
                  ? 'Confermi di voler approvare questa richiesta? L\'utente diventerà un docente e potrà creare corsi.'
                  : 'Inserisci un motivo per il rifiuto della richiesta.'}
              </DialogDescription>
            </DialogHeader>

            {actionType === 'reject' && (
              <div className="space-y-2 py-4">
                <Label htmlFor="rejection-reason">Motivo del rifiuto *</Label>
                <Textarea
                  id="rejection-reason"
                  placeholder="Spiega perché la richiesta viene rifiutata..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  disabled={processing}
                />
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedRequest(null);
                  setActionType(null);
                  setRejectionReason('');
                }}
                disabled={processing}
              >
                Annulla
              </Button>
              <Button
                onClick={confirmAction}
                disabled={processing || (actionType === 'reject' && !rejectionReason.trim())}
                className={actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                variant={actionType === 'reject' ? 'destructive' : 'default'}
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Elaborazione...
                  </>
                ) : (
                  <>
                    {actionType === 'approve' ? (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Approva
                      </>
                    ) : (
                      <>
                        <XCircle className="mr-2 h-4 w-4" />
                        Rifiuta
                      </>
                    )}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
